/**
 * Panel API istemcisi.
 *
 * Tek giriş noktası: her istek buradan geçer. Gerekçe:
 *   - **401 tek yerde işlenir.** Oturum bitince her ekran ayrı ayrı kontrol
 *     etmek zorunda kalmaz; istemci `OturumHatasi` fırlatır, kabuk giriş
 *     ekranına döner.
 *   - **Hata biçimi tek yerde çözülür.** Sunucu `{error, message, action}`
 *     gönderir; buradan `ApiHatasi` olarak çıkar ve arayüz her yerde aynı
 *     kutuyu gösterir (bkz. ErrorPanel).
 *   - Ham `err.message` KULLANICIYA GÖSTERİLMEZ; sunucunun hazırladığı
 *     Türkçe mesaj gösterilir (src/errors/presentation.ts).
 */

/** Sunucunun hata gövdesi. */
interface HataGovdesi {
  error?: string;
  message?: string;
  title?: string;
  action?: string | null;
  retryable?: boolean;
}

export class ApiHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
    message: string,
    readonly baslik?: string,
    readonly yapilacak?: string,
    readonly tekrarDenenebilir = false
  ) {
    super(message);
    this.name = 'ApiHatasi';
  }
}

/** Oturum yok/bitmiş — kabuk bunu görünce giriş ekranına döner. */
export class OturumHatasi extends ApiHatasi {
  constructor(message = 'Oturumunuz sona ermiş. Tekrar giriş yapın.') {
    super(401, 'oturum-yok', message);
    this.name = 'OturumHatasi';
  }
}

async function istek<T>(yol: string, secenekler: RequestInit = {}): Promise<T> {
  let yanit: Response;
  try {
    yanit = await fetch(yol, {
      ...secenekler,
      // Çerez aynı köken üzerinden gider (Vite proxy sayesinde geliştirmede de).
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(secenekler.body ? { 'Content-Type': 'application/json' } : {}),
        ...secenekler.headers,
      },
    });
  } catch {
    // Ağ hiç kurulamadı: sunucu kapalı ya da bağlantı yok.
    throw new ApiHatasi(
      0,
      'baglanti-yok',
      'Sunucuya ulaşılamıyor. Bağlantınızı kontrol edin; sorun sürerse sunucu kapalı olabilir.',
      'Bağlantı kurulamadı',
      'Sayfayı yenileyin. Devam ederse sistem yöneticisine bildirin.',
      true
    );
  }

  if (yanit.status === 401) throw new OturumHatasi();

  if (!yanit.ok) {
    let govde: HataGovdesi = {};
    try {
      govde = (await yanit.json()) as HataGovdesi;
    } catch {
      // Gövde JSON değil (proxy hata sayfası vb.) — genel mesaja düşülür.
    }
    throw new ApiHatasi(
      yanit.status,
      govde.error ?? 'bilinmeyen',
      govde.message ?? 'İşlem tamamlanamadı.',
      govde.title,
      govde.action ?? undefined,
      govde.retryable ?? yanit.status >= 500
    );
  }

  // 204 gövdesizdir; JSON çözmeye çalışmak hata verir.
  if (yanit.status === 204) return undefined as T;
  return (await yanit.json()) as T;
}

// ---------- Tipler (sunucudaki serialize.ts ile aynı biçim) ----------

/** Fatura DTO'ları TEK kaynaktan (sunucu da aynı dosyayı üretir) — `shared/panel-dto.ts`. */
export type {
  DurumTonu,
  FaturaSatiriOzet,
  FaturaKalemi,
  FaturaOlayi,
  FaturaDetayi,
  FaturaOnizleme,
  OnayBekleyenIs,
} from '../../../shared/panel-dto';
import type {
  FaturaSatiriOzet,
  FaturaDetayi,
  FaturaOnizleme,
  OnayBekleyenIs,
} from '../../../shared/panel-dto';

export interface Kullanici {
  email: string;
  fullName: string;
  /** Rolün görünen adı — özel roller de olabilir ("Muhasebeci"). */
  roleName: string;
  /** SUPER_ADMIN | ADMIN | OPERATOR | VIEWER | null (özel rol) */
  roleSystemKey: string | null;
  isSuperAdmin: boolean;
  /**
   * Etkin izinler (`<alan>.<eylem>`). Panel menüyü ve düğmeleri buna göre
   * çizer — ama bu bir GÖRÜNÜM kolaylığıdır, güvenlik değil. Asıl kontrol
   * her uçta sunucuda yapılır.
   */
  permissions: string[];
}

export interface OturumBilgisi {
  user: Kullanici;
  environment: string;
  isLive: boolean;
}

/** Giriş ekranının açılışta ihtiyaç duyduğu, oturum GEREKTİRMEYEN bilgiler. */
export interface AcilisBilgisi {
  /** Hiç kullanıcı yok — giriş yerine "ilk yöneticiyi oluştur" formu. */
  ilkKurulum: boolean;
  /** null ise reCAPTCHA kapalı — betik hiç yüklenmez. */
  recaptchaSiteKey: string | null;
  environment: string;
  isLive: boolean;
  /** Yalnızca TEST ortamında ve açıkça izin verilmişse dolu gelir. */
  autofill: { email: string; password: string } | null;
}

export interface FaturaListesi {
  items: FaturaSatiriOzet[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
}

// ---------- Kullanıcı yönetimi (yalnızca ADMIN) ----------

/**
 * Roller artık veritabanında; sabit birleşim tipi YOK.
 * Rol atamak için kimlik (`roleId`) kullanılır.
 */
export type RolKimligi = string;

export interface PanelKullanicisi {
  id: string;
  email: string;
  fullName: string;
  roleId: string;
  roleName: string;
  roleSystemKey: string | null;
  isSuperAdmin: boolean;
  /** Role EK olarak verilenler. */
  extraPermissions: string[];
  /** Rol verse bile geri alınanlar. */
  deniedPermissions: string[];
  /** Hesaplanmış etkin izinler — rol ∪ ekstra − geri alınan. */
  effectivePermissions: string[];
  isActive: boolean;
  locked: boolean;
  lockedUntil: string | null;
  failedAttempts: number;
  lastLoginAt: string | null;
  createdAt: string;
  /** Oturumu açık olan kişinin kendisi — kendini kapatma engellenir. */
  isSelf: boolean;
}

/** Veritabanındaki bir rol. */
export interface Rol {
  id: string;
  /** Dolu ise sistem rolü — silinemez. */
  systemKey: string | null;
  name: string;
  description: string | null;
  permissions: string[];
  userCount: number;
  deletable: boolean;
  /** Süper Yönetici rolü düzenlenemez (kendini kilitleme koruması). */
  editable: boolean;
}

export interface RolListesi {
  items: Rol[];
  totalPermissions: number;
  canEdit: boolean;
  canDelete: boolean;
}

export interface KullaniciListesi {
  items: PanelKullanicisi[];
  canEdit: boolean;
  canDelete: boolean;
}

// ---------- İzin matrisi (panel bunu çizer) ----------

export type IzinEylemi = 'goruntule' | 'duzenle' | 'sil';

export interface MatrisHucresi {
  eylem: IzinEylemi;
  /** Bu alanda bu eylem anlamlı mı? Değilse boş kutu çizilir. */
  varMi: boolean;
  izin: string | null;
  not: string | null;
  tehlikeli: boolean;
}

export interface MatrisSatiri {
  alan: string;
  etiket: string;
  aciklama: string;
  hucreler: MatrisHucresi[];
}

export interface IzinKatalogu {
  actions: Array<{ key: IzinEylemi; label: string }>;
  matrix: MatrisSatiri[];
  /** Aktörün kendi izinleri — veremeyeceği kutular kilitli gösterilir. */
  myPermissions: string[];
  canGrantAll: boolean;
  systemRoles: Array<{ systemKey: string; name: string; description: string }>;
}

// ---------- Ayarlar (salt okunur) ----------

export interface Ayarlar {
  canEdit: boolean;
  canEditSecrets: boolean;
  environment: {
    veribanEnv: string;
    isLive: boolean;
    veribanUser: string;
    earchiveUrl: string;
    einvoiceUrl: string;
    serieCode: string;
  };
  supplier: {
    vkn: string;
    title: string;
    taxOffice: string | null;
    address: string;
    website: string;
    missingFields: string[];
  };
  invoice: {
    defaultVatRate: number;
    startDate: string | null;
    archivePath: string;
    earchiveTransportType: string;
    earchiveSendSms: boolean;
  };
  security: {
    sessionHours: number;
    cookieSecure: boolean;
    devAutofill: boolean;
    recaptchaEnabled: boolean;
    recaptchaMinScore: number;
    recaptchaFailClosed: boolean;
  };
  integrations: {
    shopifyTokenTanimli: boolean;
    shopifyWebhookSecretTanimli: boolean;
    shopDomain: string | null;
  };
  worker: {
    concurrency: number;
    maxAttempts: number;
    statusPollIntervalMin: number;
  };
}

// ---------- Düzenlenebilir ayarlar ----------

export type AyarTipi = 'metin' | 'sayi' | 'secim' | 'boolean' | 'tarih' | 'sifre' | 'coksatir';

export interface AyarGorunumu {
  key: string;
  grup: string;
  etiket: string;
  aciklama: string | null;
  tip: AyarTipi;
  secenekler?: Array<{ deger: string; etiket: string }>;
  zorunlu: boolean;
  ornek: string | null;
  /** Gizli alan: değeri gelmez, yalnızca "tanımlı mı" bilgisi. */
  secret: boolean;
  deger: string;
  tanimli: boolean;
  /** Değer nereden geliyor: panelden mi sunucu dosyasından mı? */
  kaynak: 'panel' | 'env' | 'yok';
  enAz: number | null;
  enCok: number | null;
}

export interface AyarSayfasi {
  gruplar: ReadonlyArray<{ key: string; etiket: string; aciklama: string }>;
  ayarlar: AyarGorunumu[];
  canEdit: boolean;
  canEditSecrets: boolean;
  /** Şifreleme anahtarı yoksa gizli ayar kaydedilemez. */
  sifrelemeHazir: boolean;
}

// ---------- Bağlayıcılar ----------

export interface BaglayiciAlani {
  key: string;
  etiket: string;
  aciklama?: string;
  tip: 'metin' | 'sayi' | 'sifre' | 'secim' | 'boolean';
  secenekler?: Array<{ deger: string; etiket: string }>;
  secret?: boolean;
  zorunlu?: boolean;
  ornek?: string;
}

export interface Saglayici {
  key: string;
  etiket: string;
  aciklama: string;
  alanlar: BaglayiciAlani[];
}

export interface Baglayici {
  key: string;
  etiket: string;
  aciklama: string;
  /** Bu bağlantı olmadan ne çalışmaz? */
  olmazsaNeOlur: string;
  saglayicilar: Saglayici[];
  provider: string;
  isEnabled: boolean;
  config: Record<string, string>;
  /** Hangi gizli alanlar dolu (değerleri gelmez). */
  tanimliGizliAlanlar: string[];
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  lastTestMessage: string | null;
  envdenGeliyor: boolean;
}

export interface BaglayiciListesi {
  items: Baglayici[];
  canEdit: boolean;
  canEditSecrets: boolean;
  sifrelemeHazir: boolean;
}

export interface TestYaniti {
  ok: boolean;
  message: string;
  action: string | null;
  sureMs: number;
  ayrinti?: unknown;
}

// ---------- İşlem kaydı ----------

export interface DenetimKaydi {
  id: string;
  action: string;
  actionLabel: string;
  target: string | null;
  userEmail: string | null;
  userName: string | null;
  createdAt: string;
  detail: unknown;
}

export interface DenetimListesi {
  items: DenetimKaydi[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface Ozet {
  counts: Record<string, number>;
  needsAttention: number;
  /** Onaylanıp kalıcı hatayla kesilemeyen sipariş işleri (fatura kaydı yok). */
  kesilemeyenSiparis: number;
  /** Hazırlık kapısı: kontör, Veriban oturumu, Shopify — ENGEL varsa fatura kesilmez. */
  hazirlik: { hazir: boolean; kontroller: HazirlikKontrolu[]; olcumZamani: string } | null;
  queue: Record<string, number>;
  today: { count: number; total: string };
  month: { count: number; total: string };
  environment: string;
  isLive: boolean;
}

export interface ListeFiltresi {
  status?: string;
  kind?: string;
  direction?: string;
  q?: string;
  from?: string;
  to?: string;
  minAmount?: string;
  maxAmount?: string;
  hasPdf?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

// ---------- Uçlar ----------

// ---------------------------------------------------------------------------
// Hata kayıtları
// ---------------------------------------------------------------------------

/** Liste satırı — yığın izi ve ham gövde BURADA YOK, detay ucundan gelir. */
export interface HataOzeti {
  id: string;
  tarih: string;
  alan: string;
  alanEtiketi: string;
  islem: string;
  seviye: 'WARNING' | 'ERROR' | 'CRITICAL';
  seviyeEtiketi: string;
  cins: string;
  cinsEtiketi: string;
  kod: string | null;
  httpKodu: number | null;
  mesaj: string;
  tekrarDenenecek: boolean;
  deneme: number | null;
  isTipi: string | null;
  referans: string | null;
  parmakIzi: string;
  cozuldu: boolean;
  cozumTarihi: string | null;
  cozen: string | null;
  faturaKodu: string | null;
  faturaNumarasi: string | null;
  /** Gruplu görünümde aynı arızanın kaç kez tekrarladığı. */
  tekrar: number;
}

export interface HataListesi {
  items: HataOzeti[];
  gruplu: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface HataDetayi extends HataOzeti {
  nedenZinciri: Array<{ ad: string; mesaj: string }> | null;
  ayrinti: Record<string, unknown> | null;
  yigin: string | null;
  cozumNotu: string | null;
  fatura: {
    id: string;
    kod: string;
    numara: string | null;
    durum: string;
    tip: string;
    tutar: string;
    alici: string | null;
  } | null;
  digerHatalar: Array<{
    id: string;
    islem: string;
    mesaj: string;
    seviye: string;
    tarih: string;
  }>;
}

export interface HataFiltresi {
  page?: number;
  pageSize?: number;
  alan?: string;
  seviye?: string;
  durum?: string;
  q?: string;
  from?: string;
  to?: string;
  grupla?: string;
}

// ---------- Kurulum ----------

export type KurulumAdimDurumu = 'tamam' | 'eksik' | 'uyari';

export interface KurulumAdimi {
  key: string;
  etiket: string;
  durum: KurulumAdimDurumu;
  mesaj: string;
  /** Eksikliğin düzeltileceği panel yolu. */
  yol: string;
  /** Eksikliği fatura kesmeyi engeller mi? */
  engel: boolean;
}

export interface HazirlikKontrolu {
  ad: string;
  durum: 'TAMAM' | 'UYARI' | 'ENGEL';
  mesaj: string;
  yapilacak?: string;
  sureMs: number;
}

export interface KurulumDurumu {
  adimlar: KurulumAdimi[];
  faturaKesilebilir: boolean;
  engeller: string[];
  faturaSayisi: number;
  canEdit: boolean;
  /** Worker’ın son hazırlık ölçümü — henüz ölçmediyse null. */
  hazirlik: { hazir: boolean; kontroller: HazirlikKontrolu[]; olcumZamani: string } | null;
  ortam: 'test' | 'live';
}

export interface GibKontrolSonucu {
  vkn: string;
  mukellef: boolean;
  ayar: boolean;
  uyusuyor: boolean;
  mesaj: string;
  oneri: string;
}

// ---------- Şablonlar ve gönderim kayıtları ----------

export interface Sablon {
  key: string;
  kanal: 'MAIL' | 'SMS';
  ad: string;
  aciklama: string;
  alanlar: Array<{ ad: string; aciklama: string }>;
  ornekVeri: Record<string, string>;
  konu: string | undefined;
  govde: string;
  kaynak: 'panel' | 'varsayilan';
  guncellemeZamani: string | null;
}

export interface SablonOnizleme {
  kanal: 'MAIL' | 'SMS';
  konu: string | null;
  metin: string;
  html: string | null;
  sms: { alfabe: 'GSM7' | 'UCS2'; parca: number; sinir: number; uzunluk: number } | null;
  problemler: string[];
  eksikAlanlar: string[];
}

export interface GonderimKaydi {
  id: string;
  tarih: string;
  kanal: 'MAIL' | 'SMS';
  kanalEtiketi: string;
  durum: 'GONDERILDI' | 'GECICI_HATA' | 'KALICI_HATA';
  durumEtiketi: string;
  alici: string;
  konu: string | null;
  sablon: string | null;
  kaynak: string;
  saglayici: string | null;
  hata: string | null;
  faturaKodu: string | null;
  faturaNumarasi: string | null;
}

export interface WebhookAboneligi {
  id: string;
  topic: string;
  uri: string;
}

export interface WebhookKurulumSonucu {
  ok: boolean;
  mesaj: string;
  yapilacak?: string;
  adres: string | null;
  dogru: WebhookAboneligi[];
  kurulan: WebhookAboneligi[];
  yanlisAdres: WebhookAboneligi[];
  gerekli: Array<{ topic: string; aciklama: string }>;
}

export interface GonderimListesi {
  items: GonderimKaydi[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export const api = {
  acilis: () => istek<AcilisBilgisi>('/api/auth/bootstrap'),

  ilkYonetici: (govde: { fullName: string; email: string; password: string }) =>
    istek<{ ok: true }>('/api/auth/ilk-yonetici', { method: 'POST', body: JSON.stringify(govde) }),

  shopifyWebhookKur: () =>
    istek<WebhookKurulumSonucu>('/api/connectors/shopify/webhooks', { method: 'POST' }),

  girisYap: (email: string, password: string, recaptchaToken?: string | null) =>
    istek<{ user: Kullanici; expiresAt: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        ...(recaptchaToken ? { recaptchaToken } : {}),
      }),
    }),

  cikisYap: () => istek<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  oturum: () => istek<OturumBilgisi>('/api/auth/me'),

  ozet: () => istek<Ozet>('/api/summary'),

  faturalar: (filtre: ListeFiltresi = {}) => {
    const parametreler = new URLSearchParams();
    for (const [anahtar, deger] of Object.entries(filtre)) {
      if (deger !== undefined && deger !== '') parametreler.set(anahtar, String(deger));
    }
    const sorgu = parametreler.toString();
    return istek<FaturaListesi>(`/api/invoices${sorgu ? `?${sorgu}` : ''}`);
  },

  fatura: (kod: string) => istek<FaturaDetayi>(`/api/invoices/${encodeURIComponent(kod)}`),

  tekrarDene: (kod: string) =>
    istek<{ ok: true; enqueued: boolean; message: string }>(
      `/api/invoices/${encodeURIComponent(kod)}/retry`,
      { method: 'POST' }
    ),

  /** Müşteriye elle e-posta/SMS — otomatik bildirim kapalıyken. */
  musteriyeBildir: (kod: string, kanal: 'MAIL' | 'SMS') =>
    istek<{ ok: boolean; durum: string; mesaj: string }>(`/api/invoices/${encodeURIComponent(kod)}/bildir`, {
      method: 'POST',
      body: JSON.stringify({ kanal }),
    }),

  /** e-Arşiv iptal — GİB'e raporlanmamışsa; gerekçe zorunlu. */
  faturaIptal: (kod: string, neden: string) =>
    istek<{ ok: true; status: string; message: string }>(`/api/invoices/${encodeURIComponent(kod)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ neden }),
    }),
  durumYenile: (kod: string) =>
    istek<{ ok: true; enqueued: boolean; message: string }>(
      `/api/invoices/${encodeURIComponent(kod)}/refresh`,
      { method: 'POST' }
    ),

  /**
   * Belge adresi — tarayıcı doğrudan açar, çerez aynı kökende gider.
   * `surum` (kaydın updatedAt'i) adrese eklenir: tarayıcı eski bir hata yanıtını (410) önbelleğe
   * almışsa yeni adres onu atlar (ölçüldü: canlı, 11 Eylül 2026 — arşiv düzeltildi, normal pencere
   * eski hatayı göstermeye devam etti, gizli pencere açtı).
   */
  belgeAdresi: (kod: string, tip: 'pdf' | 'xml', surum?: string) =>
    `/api/invoices/${encodeURIComponent(kod)}/document/${tip}${surum ? `?v=${encodeURIComponent(surum)}` : ''}`,

  // ---------- Yönetim ----------

  kullanicilar: () => istek<KullaniciListesi>('/api/users'),

  kullaniciEkle: (govde: {
    email: string;
    fullName: string;
    roleId: RolKimligi;
    password?: string;
    extraPermissions?: string[];
    deniedPermissions?: string[];
  }) =>
    istek<{ id: string; email: string; generatedPassword: string | null; message: string }>('/api/users', {
      method: 'POST',
      body: JSON.stringify(govde),
    }),

  kullaniciSil: (id: string) =>
    istek<{ ok: true; message: string }>(`/api/users/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  izinKatalogu: () => istek<IzinKatalogu>('/api/permissions'),

  roller: () => istek<RolListesi>('/api/roles'),

  rolEkle: (govde: { name: string; description?: string; permissions: string[] }) =>
    istek<{ id: string; message: string }>('/api/roles', {
      method: 'POST',
      body: JSON.stringify(govde),
    }),

  rolGuncelle: (id: string, yama: { name?: string; description?: string; permissions?: string[] }) =>
    istek<{ ok: true; message: string }>(`/api/roles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(yama),
    }),

  rolSil: (id: string) =>
    istek<{ ok: true; message: string }>(`/api/roles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  kullaniciGuncelle: (
    id: string,
    yama: {
      fullName?: string;
      roleId?: RolKimligi;
      isActive?: boolean;
      extraPermissions?: string[];
      deniedPermissions?: string[];
    }
  ) =>
    istek<{ ok: true; message: string }>(`/api/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(yama),
    }),

  sifreSifirla: (id: string) =>
    istek<{ generatedPassword: string; message: string }>(
      `/api/users/${encodeURIComponent(id)}/reset-password`,
      { method: 'POST' }
    ),

  ayarlar: () => istek<Ayarlar>('/api/settings'),

  duzenlenebilirAyarlar: () => istek<AyarSayfasi>('/api/settings/editable'),

  ayarlariKaydet: (degerler: Record<string, string>) =>
    istek<{ ok: true; message: string }>('/api/settings/editable', {
      method: 'PUT',
      body: JSON.stringify({ degerler }),
    }),

  baglayicilar: () => istek<BaglayiciListesi>('/api/connectors'),

  baglayiciKaydet: (
    key: string,
    govde: { provider: string; isEnabled: boolean; degerler: Record<string, string> }
  ) =>
    istek<{ ok: true; message: string }>(`/api/connectors/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(govde),
    }),

  baglantiTest: (key: string) =>
    istek<TestYaniti>(`/api/connectors/${encodeURIComponent(key)}/test`, { method: 'POST' }),

  /** Gerçek test gönderimi — kullanıcının KENDİ adresine/numarasına. Ücretli. */
  testGonderimi: (key: string, hedef: string) =>
    istek<TestYaniti>(`/api/connectors/${encodeURIComponent(key)}/test-send`, {
      method: 'POST',
      body: JSON.stringify({ hedef }),
    }),

  gizliAlanTemizle: (key: string, alan: string) =>
    istek<{ ok: true; message: string }>(
      `/api/connectors/${encodeURIComponent(key)}/secret/${encodeURIComponent(alan)}`,
      { method: 'DELETE' }
    ),

  hataKayitlari: (secenekler: HataFiltresi = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(secenekler)) {
      if (v !== undefined && v !== '') p.set(k, String(v));
    }
    const sorgu = p.toString();
    return istek<HataListesi>(`/api/errors${sorgu ? `?${sorgu}` : ''}`);
  },

  hataDetayi: (id: string) => istek<HataDetayi>(`/api/errors/${encodeURIComponent(id)}`),

  hataCozuldu: (id: string, govde: { not?: string; tumGrup?: boolean }) =>
    istek<{ ok: true; kapatilan: number }>(`/api/errors/${encodeURIComponent(id)}/cozuldu`, {
      method: 'POST',
      body: JSON.stringify(govde),
    }),

  sablonlar: () => istek<{ items: Sablon[]; canEdit: boolean }>('/api/templates'),
  sablonKaydet: (key: string, govde: { konu?: string; govde: string }) =>
    istek<{ ok: true; message: string }>(`/api/templates/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(govde),
    }),
  sablonSifirla: (key: string) =>
    istek<{ ok: true; message: string }>(`/api/templates/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  sablonOnizle: (key: string, govde: { konu?: string; govde?: string }) =>
    istek<SablonOnizleme>(`/api/templates/${encodeURIComponent(key)}/onizle`, {
      method: 'POST',
      body: JSON.stringify(govde),
    }),
  sablonTestGonder: (key: string, govde: { numara?: string }) =>
    istek<{ ok: boolean; durum: string; durumEtiketi: string; mesaj: string; alici: string }>(
      `/api/templates/${encodeURIComponent(key)}/test-gonder`,
      { method: 'POST', body: JSON.stringify(govde) }
    ),
  gonderimler: (
    secenekler: { page?: number; pageSize?: number; kanal?: string; durum?: string; q?: string } = {}
  ) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(secenekler)) {
      if (v !== undefined && v !== '') p.set(k, String(v));
    }
    const sorgu = p.toString();
    return istek<GonderimListesi>(`/api/messages${sorgu ? `?${sorgu}` : ''}`);
  },

  // ---------- Onay kapısı ----------
  onayBekleyenler: (
    secenekler: {
      page?: number;
      pageSize?: number;
      eskiler?: 'goster' | 'gizle';
      kontrol?: 'hepsi' | 'kesilemez' | 'uyarili' | 'temiz';
      kargo?: 'hepsi' | 'gonderildi' | 'hazirlaniyor';
    } = {}
  ) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(secenekler)) {
      if (v !== undefined) p.set(k, String(v));
    }
    const sorgu = p.toString();
    return istek<{
      items: OnayBekleyenIs[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
      /** Onaylanmış ama henüz gönderilmemiş (kapı kapalı / sıra) — sayısı; listesi onayListesi('QUEUED'). */
      siradaSayisi: number;
      /** Onaylandı, kalıcı hatayla kesilemedi — sayısı; listesi onayListesi('DEAD'). */
      basarisizSayisi: number;
      kurulum: { faturaKesilebilir: boolean; engeller: string[] };
      hazirlik: Ozet['hazirlik'];
      /** Başlangıç tarihinden önceki bekleyen satış sayısı (varsayılan gizli). */
      baslangicOncesi: number;
      baslangicTarihi: string | null;
      eskilerGosteriliyor: boolean;
    }>(`/api/onay-bekleyen${sorgu ? `?${sorgu}` : ''}`);
  },
  /** Başlangıç tarihinden önceki bekleyenlerin tümünü atla — fatura kesmez. */
  onayBaslangicOncesiAtla: () =>
    istek<{ ok: true; atlanan: number }>('/api/onay-bekleyen/baslangic-oncesi-atla', { method: 'POST' }),
  /** Alt listeler — sunucu sayfalı. DEAD satırlarında `hata` dolu, `zaman` son deneme. */
  onayListesi: (durum: 'QUEUED' | 'DEAD', secenekler: { page?: number; pageSize?: number } = {}) => {
    const p = new URLSearchParams({ durum });
    for (const [k, v] of Object.entries(secenekler)) {
      if (v !== undefined) p.set(k, String(v));
    }
    return istek<{
      items: Array<OnayBekleyenIs & { hata: string; zaman: string }>;
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/api/onay-bekleyen/liste?${p.toString()}`);
  },
  onayYenidenAl: (id: string) =>
    istek<{ ok: true }>(`/api/onay-bekleyen/${encodeURIComponent(id)}/onaya-al`, { method: 'POST' }),
  /** Onaydan önce faturanın nasıl görüneceği (kuru koşu). */
  onayOnizleme: (id: string) =>
    istek<FaturaOnizleme>(`/api/onay-bekleyen/${encodeURIComponent(id)}/onizleme`),
  /** Adresi Shopify (teslimat adresi) üzerinde günceller, siparişi yeniden çeker, kuru koşu döner. */
  onayAdresGuncelle: (
    id: string,
    adres: { address1: string; address2: string; il: string; ilce: string; zip: string }
  ) =>
    istek<{ ok: true; engel: string | null; uyarilar: string[] }>(
      `/api/onay-bekleyen/${encodeURIComponent(id)}/adres`,
      { method: 'POST', body: JSON.stringify(adres) }
    ),
  /** Bekleyen siparişin yükünü Shopify'dan yeniden çeker; kuru koşu sonucunu döner. */
  onayYenile: (id: string) =>
    istek<{ ok: true; engel: string | null; uyarilar: string[] }>(
      `/api/onay-bekleyen/${encodeURIComponent(id)}/yenile`,
      { method: 'POST' }
    ),
  onayGeriAl: (id: string) =>
    istek<{ ok: true }>(`/api/onay-bekleyen/${encodeURIComponent(id)}/geri-al`, { method: 'POST' }),
  onayFaturala: (id: string) =>
    istek<{ ok: true; kurulumEksik: boolean; engeller: string[] }>(
      `/api/onay-bekleyen/${encodeURIComponent(id)}/faturala`,
      { method: 'POST' }
    ),
  onayAtla: (id: string, neden: string) =>
    istek<{ ok: true }>(`/api/onay-bekleyen/${encodeURIComponent(id)}/atla`, {
      method: 'POST',
      body: JSON.stringify({ neden }),
    }),

  kurulum: () => istek<KurulumDurumu>('/api/setup'),

  /** Kilit özeti — herkes okur; ayrıntı yok. */
  kurulumOzeti: () => istek<{ faturaKesilebilir: boolean; engelSayisi: number }>('/api/setup/ozet'),

  gibKontrol: () => istek<GibKontrolSonucu>('/api/settings/gib-kontrol', { method: 'POST' }),

  denetim: (secenekler: { page?: number; pageSize?: number; action?: string } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(secenekler)) {
      if (v !== undefined && v !== '') p.set(k, String(v));
    }
    const sorgu = p.toString();
    return istek<DenetimListesi>(`/api/audit${sorgu ? `?${sorgu}` : ''}`);
  },
};
